import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import type { PhotoPost, Release } from '@/types';
import {
  artworkUrl,
  fetchInfo,
  isVideoMotionArtwork,
  mediaUrl,
  resolveArtworkMotion,
} from '@/lib/api';
import { portraitSurfaceStyle } from '@/lib/portraitEffects';
import {
  DEFAULT_PORTRAIT,
  isVideoPortrait,
  resolvePortrait,
} from '@/lib/portrait';
import { ArtworkSkeleton, PortraitSkeleton } from '@/components/ui/skeleton';
import { VideoOverlay, type VideoPlaylistItem } from '@/components/player/VideoOverlay';
import { usePhotoCarouselRegister } from '@/hooks/usePhotoCarousel';

function isMediaVideo(m: { mediaUrl: string; mediaFormat?: string }): boolean {
  return (
    m.mediaFormat === 'Video' ||
    m.mediaUrl.endsWith('.mp4') ||
    /\.webm(\?|$)/i.test(m.mediaUrl)
  );
}

const IMAGE_SLIDE_MS = 4200;
const AUDIO_FADE_MS = 480;

function fadeMediaVolume(
  el: HTMLVideoElement,
  to: number,
  ms = AUDIO_FADE_MS,
): Promise<void> {
  return new Promise((resolve) => {
    const from = el.volume;
    if (ms <= 0 || Math.abs(from - to) < 0.01) {
      el.volume = to;
      resolve();
      return;
    }
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / ms);
      el.volume = from + (to - from) * t;
      if (t < 1) requestAnimationFrame(tick);
      else resolve();
    };
    requestAnimationFrame(tick);
  });
}

function preloadPortrait(src: string): Promise<void> {
  if (isVideoPortrait(src)) {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'auto';
      video.muted = true;
      const done = () => resolve();
      video.addEventListener('loadeddata', done, { once: true });
      video.addEventListener('error', done, { once: true });
      video.src = src;
    });
  }
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = src;
  });
}

type Props = {
  mode: 'music' | 'photo' | 'info' | 'settings';
  release: Release | null;
  photo: PhotoPost | null;
  detail: boolean;
  video: VideoPlaylistItem | null;
  videoPlaylist?: VideoPlaylistItem[];
  onSelectVideo?: (item: VideoPlaylistItem) => void;
  onCloseVideo: () => void;
};

export function MediaPanel({
  mode,
  release,
  photo,
  detail,
  video,
  videoPlaylist = [],
  onSelectVideo,
  onCloseVideo,
}: Props) {
  /** Track position in the tripled endless strip (middle copy starts at `n`). */
  const [track, setTrack] = useState(0);
  const [fadeIn, setFadeIn] = useState(false);
  const [muted, setMuted] = useState(true);
  const [dragX, setDragX] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [slideshowPlaying, setSlideshowPlaying] = useState(false);
  /** After muted autoplay succeeds, allow React to keep the slide unmuted. */
  const [slideAudioOn, setSlideAudioOn] = useState(false);
  const [artReady, setArtReady] = useState(false);
  const [ambientReady, setAmbientReady] = useState(false);
  const [portraitSrc, setPortraitSrc] = useState<string | null>(null);
  const [siteInfo, setSiteInfo] = useState<Awaited<ReturnType<typeof fetchInfo>> | null>(
    null,
  );
  const startX = useRef<number | null>(null);
  const dragging = useRef(false);
  const skipTransition = useRef(false);
  const slideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeVideoRef = useRef<HTMLVideoElement | null>(null);
  const fadeGenRef = useRef(0);
  const trackRef = useRef(0);
  const registerCarousel = usePhotoCarouselRegister();
  const portraitStyle = useMemo(
    () => (siteInfo ? portraitSurfaceStyle(siteInfo) : {}),
    [siteInfo],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const info = await fetchInfo();
        const src = resolvePortrait(info.portraitUrl);
        await preloadPortrait(src);
        if (cancelled) return;
        setSiteInfo(info);
        setPortraitSrc(src);
        requestAnimationFrame(() => {
          if (!cancelled) setFadeIn(true);
        });
      } catch {
        const src = DEFAULT_PORTRAIT;
        await preloadPortrait(src);
        if (cancelled) return;
        setSiteInfo(null);
        setPortraitSrc(src);
        requestAnimationFrame(() => {
          if (!cancelled) setFadeIn(true);
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleMedia = useMemo(
    () => (photo?.media || []).filter((m) => m && m.hidden !== true),
    [photo],
  );
  const firstPhotoSrc = visibleMedia[0]
    ? mediaUrl(visibleMedia[0].mediaUrl)
    : '';
  const firstPhotoIsVideo = Boolean(
    visibleMedia[0] &&
      (visibleMedia[0].mediaFormat === 'Video' ||
        /\.(mp4|webm)(\?|$)/i.test(visibleMedia[0].mediaUrl)),
  );

  const n = visibleMedia.length;
  const endless = n > 1;
  /** Logical 0-based slide index */
  const index = n ? ((track % n) + n) % n : 0;
  trackRef.current = track;

  const clearSlideTimer = useCallback(() => {
    if (slideTimerRef.current) {
      clearTimeout(slideTimerRef.current);
      slideTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    // Land in the middle copy so we can move either direction endlessly.
    setTrack(endless ? n : 0);
    setDragX(0);
    setAnimating(false);
    setSlideshowPlaying(false);
    setSlideAudioOn(false);
    clearSlideTimer();
    skipTransition.current = true;
  }, [release?.id, photo?.id, n, endless, clearSlideTimer]);

  useLayoutEffect(() => {
    if (!skipTransition.current) return;
    const id = requestAnimationFrame(() => {
      skipTransition.current = false;
    });
    return () => cancelAnimationFrame(id);
  }, [track]);

  // Back to catalog: portrait must be opaque in the VT “new” snapshot.
  useLayoutEffect(() => {
    if (detail || !portraitSrc) return;
    setFadeIn(true);
  }, [detail, portraitSrc]);

  // Reveal art before VT capture: useLayoutEffect runs under flushSync in
  // startViewTransition, so a warm/cached image is opacity:1 in the “new”
  // media snapshot (useEffect was too late → empty frame blink on desktop).
  useLayoutEffect(() => {
    if (detail && mode === 'music') {
      const motion = resolveArtworkMotion(release?.artworkMotionUrl);
      if (isVideoMotionArtwork(motion)) {
        setArtReady(false);
        const video = document.createElement('video');
        const done = () => setArtReady(true);
        video.addEventListener('loadeddata', done, { once: true });
        video.addEventListener('error', done, { once: true });
        video.muted = true;
        video.playsInline = true;
        video.preload = 'auto';
        video.src = motion;
        return;
      }
      if (motion && /\.gif(\?|$)/i.test(motion)) {
        const img = new Image();
        const done = () => setArtReady(true);
        img.onload = done;
        img.onerror = done;
        img.src = motion;
        if (img.complete) done();
        else setArtReady(false);
        return;
      }
      if (release?.artworkUrl) {
        const src = artworkUrl(release.artworkUrl);
        const img = new Image();
        const done = () => setArtReady(true);
        img.onload = done;
        img.onerror = done;
        img.src = src;
        if (img.complete) done();
        else setArtReady(false);
      }
      return;
    }
    if (detail && mode === 'photo' && firstPhotoSrc) {
      if (firstPhotoIsVideo) {
        setArtReady(false);
        const video = document.createElement('video');
        const done = () => setArtReady(true);
        video.addEventListener('loadeddata', done, { once: true });
        video.addEventListener('error', done, { once: true });
        video.preload = 'auto';
        video.src = firstPhotoSrc;
        return;
      }
      const img = new Image();
      const done = () => setArtReady(true);
      img.onload = done;
      img.onerror = done;
      img.src = firstPhotoSrc;
      if (img.complete) done();
      else setArtReady(false);
    }
  }, [
    detail,
    mode,
    release?.id,
    release?.artworkUrl,
    release?.artworkMotionUrl,
    photo?.id,
    firstPhotoSrc,
    firstPhotoIsVideo,
  ]);

  const stepCarousel = useCallback(
    (delta: number) => {
      if (!endless) return;
      clearSlideTimer();
      setAnimating(true);
      setTrack((t) => t + delta);
    },
    [endless, clearSlideTimer],
  );

  const normalizeTrack = useCallback(() => {
    if (!endless) return;
    setTrack((t) => {
      if (t < n) {
        skipTransition.current = true;
        return t + n;
      }
      if (t >= 2 * n) {
        skipTransition.current = true;
        return t - n;
      }
      return t;
    });
    setAnimating(false);
  }, [endless, n]);

  const currentIsVideo = Boolean(
    visibleMedia[index] && isMediaVideo(visibleMedia[index]!),
  );

  const fadeOutActiveVideo = useCallback(async () => {
    const el = activeVideoRef.current;
    if (!el) return;
    const gen = ++fadeGenRef.current;
    await fadeMediaVolume(el, 0, AUDIO_FADE_MS);
    if (gen !== fadeGenRef.current) return;
    el.pause();
    el.muted = true;
  }, []);

  const togglePlay = useCallback(() => {
    if (!n) return;
    setSlideshowPlaying((on) => {
      if (on) {
        clearSlideTimer();
        setSlideAudioOn(false);
        void fadeOutActiveVideo();
        return false;
      }
      return true;
    });
  }, [n, clearSlideTimer, fadeOutActiveVideo]);

  // Slideshow: images advance on a timer; videos play through once, then advance.
  useEffect(() => {
    clearSlideTimer();
    setSlideAudioOn(false);
    if (!slideshowPlaying || !detail || mode !== 'photo' || !n) return;

    let cancelled = false;
    let endedHandler: (() => void) | null = null;
    let videoEl: HTMLVideoElement | null = null;

    const advance = () => {
      if (cancelled) return;
      setSlideAudioOn(false);
      if (endless) stepCarousel(1);
      else setSlideshowPlaying(false);
    };

    const startImageDwell = () => {
      if (!endless) {
        setSlideshowPlaying(false);
        return;
      }
      slideTimerRef.current = setTimeout(advance, IMAGE_SLIDE_MS);
    };

    const startVideoOnce = async (el: HTMLVideoElement) => {
      videoEl = el;
      const gen = ++fadeGenRef.current;
      el.loop = false;
      try {
        el.currentTime = 0;
      } catch {
        /* ignore seek errors before ready */
      }

      // Browsers block unmuted autoplay after a slide change — start muted,
      // then unmute and fade audio once playback has begun.
      el.muted = true;
      el.volume = 0;
      try {
        await el.play();
      } catch {
        await new Promise<void>((resolve) => {
          if (el.readyState >= 2) {
            resolve();
            return;
          }
          const done = () => resolve();
          el.addEventListener('canplay', done, { once: true });
          el.addEventListener('error', done, { once: true });
        });
        try {
          await el.play();
        } catch {
          if (!cancelled && endless) startImageDwell();
          return;
        }
      }
      if (cancelled || gen !== fadeGenRef.current) return;

      el.muted = false;
      setSlideAudioOn(true);
      await fadeMediaVolume(el, 1, AUDIO_FADE_MS);
      if (cancelled || gen !== fadeGenRef.current) return;

      endedHandler = () => {
        if (cancelled || gen !== fadeGenRef.current) return;
        void (async () => {
          await fadeMediaVolume(el, 0, AUDIO_FADE_MS);
          if (cancelled || gen !== fadeGenRef.current) return;
          el.muted = true;
          setSlideAudioOn(false);
          advance();
        })();
      };
      el.addEventListener('ended', endedHandler);
    };

    if (currentIsVideo) {
      let tries = 0;
      const waitForVideo = () => {
        if (cancelled) return;
        const el = activeVideoRef.current;
        // Prefer the strip position that matches `track` (visible clone).
        if (el && el.dataset.carouselAbs === String(trackRef.current)) {
          void startVideoOnce(el);
          return;
        }
        if (el && tries > 6) {
          void startVideoOnce(el);
          return;
        }
        if (tries++ < 50) {
          slideTimerRef.current = setTimeout(waitForVideo, 40);
        } else {
          startImageDwell();
        }
      };
      // Wait for the strip transition to land the visible clone’s ref.
      slideTimerRef.current = setTimeout(waitForVideo, 60);
    } else {
      startImageDwell();
    }

    return () => {
      cancelled = true;
      clearSlideTimer();
      setSlideAudioOn(false);
      if (videoEl && endedHandler) {
        videoEl.removeEventListener('ended', endedHandler);
        const gen = ++fadeGenRef.current;
        void fadeMediaVolume(videoEl, 0, Math.min(220, AUDIO_FADE_MS)).then(
          () => {
            if (gen !== fadeGenRef.current) return;
            videoEl?.pause();
            if (videoEl) videoEl.muted = true;
          },
        );
      }
    };
  }, [
    slideshowPlaying,
    index,
    currentIsVideo,
    detail,
    mode,
    n,
    endless,
    stepCarousel,
    clearSlideTimer,
  ]);

  useEffect(() => {
    if (!detail || mode !== 'photo' || !n) {
      registerCarousel(null);
      return;
    }
    registerCarousel({
      count: n,
      index,
      canSlide: endless,
      isVideo: currentIsVideo,
      playing: slideshowPlaying,
      prev: () => stepCarousel(-1),
      next: () => stepCarousel(1),
      togglePlay,
    });
    return () => registerCarousel(null);
  }, [
    detail,
    mode,
    n,
    index,
    endless,
    currentIsVideo,
    slideshowPlaying,
    stepCarousel,
    togglePlay,
    registerCarousel,
  ]);

  const onPointerDown = (clientX: number) => {
    if (!endless) return;
    dragging.current = true;
    startX.current = clientX;
    setAnimating(false);
  };
  const onPointerMove = (clientX: number) => {
    if (!dragging.current || startX.current == null) return;
    setDragX(clientX - startX.current);
  };
  const onPointerUp = (clientX: number) => {
    if (!dragging.current || startX.current == null) return;
    const dx = clientX - startX.current;
    dragging.current = false;
    startX.current = null;
    setDragX(0);
    if (Math.abs(dx) > 40) stepCarousel(dx < 0 ? 1 : -1);
    else setAnimating(true);
  };

  const showPhotoVideo = currentIsVideo;

  const loopSlides = useMemo(() => {
    if (!n) return [];
    if (!endless) {
      return visibleMedia.map((m, i) => ({
        m,
        key: `${m.id}-0`,
        logical: i,
        abs: i,
      }));
    }
    return [0, 1, 2].flatMap((copy) =>
      visibleMedia.map((m, i) => ({
        m,
        key: `${m.id}-${copy}`,
        logical: i,
        abs: copy * n + i,
      })),
    );
  }, [visibleMedia, n, endless]);

  const isPortrait = !detail;
  // Reserve release/photo stage as soon as the route is detail — don't wait on data.
  const isRelease = Boolean(detail && mode === 'music');
  const isPhoto = Boolean(detail && mode === 'photo');
  const panelClass = isRelease
    ? 'selected-visual release-visual'
    : isPhoto
      ? 'selected-visual photo-visual'
      : 'portrait-panel';
  const releaseArtSrc =
    isRelease && release?.artworkUrl ? artworkUrl(release.artworkUrl) : '';
  const releaseMotionSrc =
    isRelease && release?.artworkMotionUrl
      ? resolveArtworkMotion(release.artworkMotionUrl)
      : '';
  const releaseMotionVideo = isVideoMotionArtwork(releaseMotionSrc);
  const releaseMotionGif = Boolean(
    releaseMotionSrc && /\.gif(\?|$)/i.test(releaseMotionSrc),
  );
  const releaseHasArt = Boolean(
    releaseMotionSrc || releaseArtSrc,
  );
  const showArtSkeleton =
    (isRelease && (!releaseHasArt || !artReady)) ||
    (isPhoto && (!visibleMedia.length || !artReady));

  /** Still-image source for the tablet ambient blur fill (never video). */
  const ambientSrc = useMemo(() => {
    if (isRelease) return releaseArtSrc || '';
    if (isPhoto) {
      const current = visibleMedia[index];
      if (current && !isMediaVideo(current)) return mediaUrl(current.mediaUrl);
      const still = visibleMedia.find((m) => !isMediaVideo(m));
      return still ? mediaUrl(still.mediaUrl) : '';
    }
    if (portraitSrc && !isVideoPortrait(portraitSrc)) return portraitSrc;
    return '';
  }, [
    isRelease,
    isPhoto,
    releaseArtSrc,
    visibleMedia,
    index,
    portraitSrc,
  ]);
  useEffect(() => {
    setAmbientReady(false);
  }, [ambientSrc]);

  return (
    <section
      className={`visual-panel ${panelClass}${ambientSrc ? ' has-ambient' : ''}`}
      aria-label="Media"
    >
      {/* Tablet / small-desktop only (CSS): blurred cover fill behind the square */}
      <div
        className={`art-ambient${ambientSrc && ambientReady ? ' is-on' : ''}`}
        aria-hidden
      >
        {ambientSrc ? (
          <img
            key={ambientSrc}
            src={ambientSrc}
            alt=""
            draggable={false}
            decoding="async"
            ref={(el) => {
              if (el?.complete && el.naturalWidth > 0) setAmbientReady(true);
            }}
            onLoad={() => setAmbientReady(true)}
            onError={() => setAmbientReady(false)}
          />
        ) : null}
      </div>
      <div
        className={`media-stage${isPhoto && endless ? ' is-swipeable' : ''}`}
        onMouseDown={(e) => onPointerDown(e.clientX)}
        onMouseMove={(e) => onPointerMove(e.clientX)}
        onMouseUp={(e) => onPointerUp(e.clientX)}
        onMouseLeave={(e) => {
          if (dragging.current) onPointerUp(e.clientX);
        }}
        onTouchStart={(e) => onPointerDown(e.touches[0]?.clientX || 0)}
        onTouchMove={(e) => onPointerMove(e.touches[0]?.clientX || 0)}
        onTouchEnd={(e) => onPointerUp(e.changedTouches[0]?.clientX || 0)}
      >
        {/* Persistent portrait host: visibility-hidden (not unmounted) so the
            gradient animation keeps its timeline across view changes. */}
        <div
          className={`portrait-layer${isPortrait ? '' : ' is-hidden'}`}
          style={portraitStyle}
          aria-hidden={!isPortrait}
        >
          {!fadeIn && isPortrait ? <PortraitSkeleton /> : null}
          {portraitSrc ? (
            isVideoPortrait(portraitSrc) ? (
              <video
                key={portraitSrc}
                className="portrait-still"
                autoPlay
                muted
                loop
                playsInline
                style={{
                  opacity: fadeIn ? 1 : 0,
                  transition: 'opacity 0.45s ease',
                }}
              >
                <source src={portraitSrc} />
              </video>
            ) : (
              <img
                key={portraitSrc}
                src={portraitSrc}
                alt=""
                className="portrait-still"
                style={{
                  opacity: fadeIn ? 1 : 0,
                  transition: 'opacity 0.45s ease',
                }}
              />
            )
          ) : null}
        </div>

        {showArtSkeleton ? (
          <ArtworkSkeleton
            label={isPhoto ? 'Loading photo' : 'Loading artwork'}
          />
        ) : null}

        {isRelease && releaseMotionVideo ? (
          <video
            key={releaseMotionSrc}
            className="release-art"
            src={releaseMotionSrc}
            poster={releaseArtSrc || undefined}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-label={`${release?.name || 'Release'} artwork`}
            onLoadedData={() => setArtReady(true)}
            onError={() => setArtReady(true)}
            style={{ opacity: artReady ? 1 : 0 }}
          />
        ) : isRelease && releaseMotionGif ? (
          <img
            key={releaseMotionSrc}
            className="release-art"
            src={releaseMotionSrc}
            alt={`${release?.name || 'Release'} artwork`}
            onLoad={() => setArtReady(true)}
            onError={() => setArtReady(true)}
            style={{ opacity: artReady ? 1 : 0 }}
          />
        ) : isRelease && releaseArtSrc ? (
          <img
            className="release-art"
            src={releaseArtSrc}
            alt={`${release?.name || 'Release'} artwork`}
            onLoad={() => setArtReady(true)}
            onError={() => setArtReady(true)}
            style={{ opacity: artReady ? 1 : 0 }}
          />
        ) : null}

        {isPhoto && photo && loopSlides.length ? (
          <div
            className={`photo-carousel${artReady ? ' is-ready' : ''}`}
            style={{
              transform: `translateX(calc(${-track * 100}% + ${dragX}px))`,
              transition:
                skipTransition.current || dragging.current || !animating
                  ? 'none'
                  : 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)',
            }}
            onTransitionEnd={() => {
              if (skipTransition.current) {
                skipTransition.current = false;
                return;
              }
              normalizeTrack();
            }}
          >
            {loopSlides.map(({ m, key, logical, abs }) => (
              <div key={key} className="photo-carousel-item">
                {isMediaVideo(m) ? (
                  <video
                    autoPlay={!slideshowPlaying && abs === track}
                    muted={
                      abs !== track
                        ? true
                        : slideshowPlaying
                          ? !slideAudioOn
                          : muted
                    }
                    loop={!slideshowPlaying}
                    playsInline
                    preload="auto"
                    src={mediaUrl(m.mediaUrl)}
                    data-carousel-abs={abs}
                    ref={(el) => {
                      // Bind to the visible strip cell only (not off-screen clones).
                      if (abs !== track) return;
                      activeVideoRef.current = el;
                      if (!el || slideshowPlaying) return;
                      el.loop = true;
                      el.muted = true;
                      if (!el.paused) return;
                      void el.play().catch(() => {});
                    }}
                    onLoadedData={() => {
                      if (abs === track) setArtReady(true);
                    }}
                    onError={() => {
                      if (abs === track) setArtReady(true);
                    }}
                  />
                ) : (
                  <img
                    src={mediaUrl(m.mediaUrl)}
                    alt={`${photo.title}, ${logical + 1}`}
                    draggable={false}
                    onLoad={() => {
                      if (abs === track) setArtReady(true);
                    }}
                    onError={() => {
                      if (abs === track) setArtReady(true);
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        ) : null}

        {video ? (
          <VideoOverlay
            videoUrl={video.url}
            track={video.track}
            artwork={video.artwork}
            album={video.album}
            artist={video.artist}
            playlist={videoPlaylist}
            onSelectVideo={onSelectVideo}
            onClose={onCloseVideo}
          />
        ) : null}

        {showPhotoVideo ? (
          <button
            type="button"
            className="photo-mute"
            aria-label={muted ? 'Unmute photo video' : 'Mute photo video'}
            onClick={() => {
              setMuted((m) => {
                const next = !m;
                sessionStorage.setItem('videoMuted', next ? 'true' : 'false');
                return next;
              });
            }}
          >
            {muted ? (
              <VolumeX size={16} />
            ) : (
              <Volume2 size={16} />
            )}
          </button>
        ) : null}
      </div>
    </section>
  );
}
